import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, CloudRain, Sun, Cloud, Thermometer, Wind, Droplets, Sprout, AlertTriangle, Download, MapPin } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const regionalForecasts = [{
  region: 'Central Region',
  districts: 'Kampala, Wakiso, Mukono, Masaka',
  weather: 'Scattered Thunderstorms',
  temp: '24°C - 28°C',
  rainfall: 'Moderate to Heavy',
  icon: CloudRain,
  advisory: 'Good soil moisture levels. Farmers are advised to complete planting of maize and beans. Ensure proper drainage in low-lying areas to prevent waterlogging.',
  status: 'Favorable'
}, {
  region: 'Western Region',
  districts: 'Mbarara, Kabale, Kasese, Hoima',
  weather: 'Partly Cloudy',
  temp: '18°C - 26°C',
  rainfall: 'Light Showers',
  icon: Cloud,
  advisory: 'Ideal conditions for harvesting mature crops. Dairy farmers should harvest and preserve pasture (silage/hay) while weather permits.',
  status: 'Normal'
}, {
  region: 'Northern Region',
  districts: 'Gulu, Lira, Arua, Kitgum',
  weather: 'Sunny Intervals',
  temp: '28°C - 34°C',
  rainfall: 'Below Average',
  icon: Sun,
  advisory: 'Dry spells expected. Farmers should practice mulching and water conservation. Delay planting until steady rains are established.',
  status: 'Caution'
}, {
  region: 'Eastern Region',
  districts: 'Jinja, Mbale, Soroti, Tororo',
  weather: 'Heavy Rain',
  temp: '22°C - 27°C',
  rainfall: 'Above Average',
  icon: CloudRain,
  advisory: 'High risk of flooding in Elgon sub-region. Avoid planting in flood-prone areas. Coffee farmers should monitor for fungal diseases due to high humidity.',
  status: 'Alert'
}];
export function WeatherAdvisoryPage() {
  return <>
      <Header data-id="element-1703" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1704">
        {/* Hero Section */}
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1705">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/jPhhaNQoaDwUV6MUpbUDU5/fdhjkl_(9).jpg")'
        }} data-id="element-1706"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1707"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1708">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1709">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1710">
                <Home className="w-4 h-4 mr-1" data-id="element-1711" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1712" />
              <span data-id="element-1713">Resources</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1714" />
              <span className="text-white font-medium" data-id="element-1715">Weather Advisory</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1716">
              Agro-Meteorological Advisory
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1717">
              Timely weather forecasts and tailored farming advisories to help
              Ugandan farmers make informed decisions and build climate
              resilience.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1718">
          {/* National Overview */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12" data-id="element-1719">
            <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-1720">
              <div className="md:w-1/3 text-center md:text-left border-b md:border-b-0 md:border-r border-gray-200 pb-8 md:pb-0 md:pr-8" data-id="element-1721">
                <h2 className="text-2xl font-bold text-uganda-black mb-2" data-id="element-1722">
                  National Overview
                </h2>
                <p className="text-gray-500 text-sm mb-6" data-id="element-1723">
                  Valid for: May 2024
                </p>
                <div className="flex items-center justify-center md:justify-start gap-4 text-uganda-green" data-id="element-1724">
                  <CloudRain className="w-16 h-16" data-id="element-1725" />
                  <div data-id="element-1726">
                    <div className="text-4xl font-bold" data-id="element-1727">Onset</div>
                    <div className="text-sm font-medium uppercase tracking-wider" data-id="element-1728">
                      First Season Rains
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:w-2/3" data-id="element-1729">
                <h3 className="text-lg font-bold text-uganda-black mb-3" data-id="element-1730">
                  General Advisory
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-1731">
                  The first major rainy season (MAM) has fully established
                  across most parts of the country. Farmers are advised to
                  maximize this period for planting staple crops. However,
                  localized heavy storms may cause waterlogging in poorly
                  drained soils. Livestock farmers should ensure animals have
                  shelter during heavy downpours to prevent pneumonia.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1732">
                  <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3" data-id="element-1733">
                    <Thermometer className="w-5 h-5 text-red-500" data-id="element-1734" />
                    <div data-id="element-1735">
                      <div className="text-xs text-gray-500" data-id="element-1736">Avg Temp</div>
                      <div className="font-bold text-gray-900" data-id="element-1737">25°C</div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3" data-id="element-1738">
                    <Droplets className="w-5 h-5 text-blue-500" data-id="element-1739" />
                    <div data-id="element-1740">
                      <div className="text-xs text-gray-500" data-id="element-1741">Humidity</div>
                      <div className="font-bold text-gray-900" data-id="element-1742">78%</div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3" data-id="element-1743">
                    <Wind className="w-5 h-5 text-gray-500" data-id="element-1744" />
                    <div data-id="element-1745">
                      <div className="text-xs text-gray-500" data-id="element-1746">Wind</div>
                      <div className="font-bold text-gray-900" data-id="element-1747">12 km/h</div>
                    </div>
                  </div>
                  <button className="bg-uganda-light-green text-uganda-green hover:bg-green-100 p-3 rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors text-sm" data-id="element-1748">
                    <Download className="w-4 h-4" data-id="element-1749" /> Bulletin
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Regional Forecasts */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1750">
            Regional Farming Advisories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1751">
            {regionalForecasts.map((region, index) => {
            const Icon = region.icon;
            return <motion.div key={region.region} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1752">
                  <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-start" data-id="element-1753">
                    <div data-id="element-1754">
                      <h3 className="text-xl font-bold text-uganda-black mb-1" data-id="element-1755">
                        {region.region}
                      </h3>
                      <div className="flex items-center text-sm text-gray-500" data-id="element-1756">
                        <MapPin className="w-3.5 h-3.5 mr-1" data-id="element-1757" />
                        {region.districts}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${region.status === 'Favorable' ? 'bg-green-100 text-green-800' : region.status === 'Normal' ? 'bg-blue-100 text-blue-800' : region.status === 'Caution' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-1758">
                      {region.status}
                    </div>
                  </div>
                  <div className="p-6" data-id="element-1759">
                    <div className="flex items-center gap-6 mb-6" data-id="element-1760">
                      <div className="flex items-center gap-3" data-id="element-1761">
                        <Icon className="w-8 h-8 text-gray-700" data-id="element-1762" />
                        <div data-id="element-1763">
                          <div className="text-sm font-semibold text-gray-900" data-id="element-1764">
                            {region.weather}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1765">
                            {region.temp}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-uganda-light-green/50 rounded-lg p-4 border border-green-100" data-id="element-1766">
                      <div className="flex items-start gap-3" data-id="element-1767">
                        <Sprout className="w-5 h-5 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-1768" />
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1769">
                          <span className="font-semibold text-uganda-green block mb-1" data-id="element-1770">
                            Advisory:
                          </span>
                          {region.advisory}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </main>
      <Footer data-id="element-1771" />
    </>;
}